import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../../../components/Layouts/Layouts/Layout";
import EPlatformHeader from "../../../components/basic/EPlatformHeader";
import { Button } from "react-bootstrap";
import axios from "axios";
import toast from "react-hot-toast";
import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  validateCaptcha,
} from "react-simple-captcha";
import { getURLbyEndPointV2 } from "../../../store/api";
import "../../../pages/auth/Register/Register.css";
import { useAuth } from "../../../context/auth";
import { storeAuthUser } from "../../../store/userStore";
import { storeAuthUserlocal } from "../../../store/services";

const IACLogin = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    claimStatement: null,
  });

  const location = useLocation();
  const [auth, setAuth] = useAuth();
  const [queryParams, setQueryParams] = useState({});

  const [captchaValue, setCaptchaValue] = useState("");
  const [isCaptchaCorrect, setIsCaptchaCorrect] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const captcha_length = 6;

  // Extract query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get("email") || "";
    const disputeId = params.get("disputeId") || "";
    const partyId = params.get("partyId") || "";
    const requestFor = params.get("requestFor") || "";

    if (email) {
      setFormData((prev) => ({ ...prev, email }));
    }
    setQueryParams({ disputeId, partyId, requestFor });
  }, [location]);

  // CAPTCHA setup
  useEffect(() => {
    loadCaptchaEnginge(captcha_length);
  }, []);

  const handleCaptchaChange = (evt) => {
    setCaptchaValue(evt.target.value);
    setIsCaptchaCorrect(validateCaptcha(evt.target.value, false));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      // Check for PDF
      if (files[0].type !== "application/pdf") {
        toast.error("Only PDF files are allowed");
        e.target.value = null; // Reset file input
        return;
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: files[0],
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      claimStatement: null,
    });
    setCaptchaValue("");
    setIsCaptchaCorrect(false);
    loadCaptchaEnginge(captcha_length);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSuccess(true);
    try {
      if (!isCaptchaCorrect) {
        toast.error("CAPTCHA is invalid");
        return;
      }

      // Email Validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email || !emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address");
        return;
      }

      // Password Validation
      const validatePassword = (password) => {
        const regex =
          /^(?=.*[a-z])(?=.*\d)(?=.*[A-Z]?)(?=.*[!@#$%^&*()_+[\]{}|;:',.<>?~\\/-]).{8,15}$/;
        return regex.test(password);
      };

      if (!validatePassword(formData.password)) {
        alert(
          "Password must be 8–15 chars, contain lowercase, number & special character."
        );
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("email", formData.email);
      formDataToSend.append("password", formData.password);
      if (formData.claimStatement) {
        formDataToSend.append("claimStatement", formData.claimStatement);
      }
      if (queryParams.disputeId)
        formDataToSend.append("disputeId", queryParams.disputeId);
      if (queryParams.partyId)
        formDataToSend.append("partyId", queryParams.partyId);
      if (queryParams.requestFor)
        formDataToSend.append("requestFor", queryParams.requestFor);

      // Use iacLogin endpoint for existing users login flow
      const res = await axios.post(
        getURLbyEndPointV2("iacLogin"),
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res?.data?.status) {
        toast.success(res.data.message);
        const data = res.data.data; // tokenInfo
        const info = res.data.info; // client info

        if (data && data.token) {
          storeAuthUser(info);
          storeAuthUserlocal(info);

          setAuth({
            ...auth,
            user: data.userId,
            token: data.token,
          });
          localStorage.setItem("auth", JSON.stringify(data));

          // Redirect to dashboard
          setTimeout(() => {
            window.location.href = "/User-dashboard";
          }, 1000);
        } else {
          resetForm();
        }
      } else {
        toast.error(res?.data?.message || "An error occurred");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(error?.response?.data?.message || "An error occurred");
    } finally {
      setFormSuccess(false);
    }
  };

  return (
    <Layout>
      <EPlatformHeader heading="E-Platform IAC - Login / Claim Submission" />
      <div className="d-flex justify-content-center">
        <form
          className="main-width my-3 d-flex flex-column justify-content-start align-items-start"
          onSubmit={handleFormSubmit}
        >
          <h1>Claimant Login</h1>
          <p>
            Please enter your credentials and upload your claim statement if
            applicable.
          </p>

          {/* Email */}
          <div className="w-100 mb-3 d-flex justify-content-between align-items-start mt-4">
            <label className="fw-semibold fs-6">*EMAIL</label>
            <input
              style={{ width: "35vw" }}
              className="form-control rounded-0"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Password */}
          <div className="w-100 mb-3 d-flex justify-content-between align-items-start">
            <label className="fw-semibold fs-6">*PASSWORD</label>
            <input
              style={{ width: "35vw" }}
              className="form-control rounded-0"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Claim Statement */}
          <h3 className="my-4">CLAIM STATEMENT</h3>
          <div className="w-100 mb-3 d-flex justify-content-between align-items-start">
            <label className="fw-semibold fs-6">
              YOUR CLAIM STATEMENT (PDF)
            </label>
            <input
              style={{ width: "35vw" }}
              className="form-control rounded-0"
              name="claimStatement"
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
            />
          </div>

          {/* CAPTCHA */}
          <h3 className="my-4">SECURITY CHECK</h3>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <LoadCanvasTemplate />
            <input
              style={{ width: "35vw" }}
              className="form-control rounded-0 ms-3"
              placeholder="Type the given captcha word"
              name="captcha"
              value={captchaValue}
              onChange={handleCaptchaChange}
              maxLength={captcha_length}
              required
            />
          </div>
          <p className={isCaptchaCorrect ? "text-success" : "text-danger"}>
            {isCaptchaCorrect ? "CAPTCHA is valid" : "CAPTCHA is invalid"}
          </p>

          <div className="d-flex justify-content-center gap-4 my-4">
            <Button
              variant="secondary"
              className="px-5 py-2 rounded-1 border-0 fw-medium"
              onClick={resetForm}
            >
              RESET
            </Button>
            <Button
              type="submit"
              className="setup-bg-color px-5 py-2 rounded-1 border-0 fw-medium"
              disabled={formSuccess}
            >
              {!formSuccess ? "SUBMIT" : "PROCESSING..."}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default IACLogin;
